// First-party referral memory for Creator Partner links.
// Stores only the most recent valid creator click (last click wins) for the
// attribution window, and hands it to checkout. It never identifies the buyer.
import { api } from "./api";

const KEY = "instapass_creator_ref";
const WINDOW_DAYS = 7; // mirrors CREATOR_ATTRIBUTION_DAYS on the server (the server enforces it)
const CODE_RE = /^CREATOR-\d{3,7}-[A-Z0-9]{3,8}$/;

export function saveReferral({ code, clickId, promoCode }) {
  if (!code || !clickId) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ code, clickId, promoCode: promoCode || null, at: Date.now() }));
  } catch {}
}

/** The creator's TEVO promo code from the last creator link, for pre-filling checkout. */
export function getCreatorPromo() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "null");
    if (!raw?.promoCode || Date.now() - raw.at > WINDOW_DAYS * 86400000) return null;
    return raw.promoCode;
  } catch {
    return null;
  }
}

export function getActiveReferral() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "null");
    if (!raw?.code || !raw?.clickId) return null;
    if (Date.now() - raw.at > WINDOW_DAYS * 86400000) {
      localStorage.removeItem(KEY);
      return null;
    }
    return { code: raw.code, clickId: raw.clickId };
  } catch {
    return null;
  }
}

/** Record a click and remember it. Returns the event path to send the visitor to. */
export async function trackReferral(code, { kind = "click", channel } = {}) {
  const normalized = String(code || "").toUpperCase();
  if (!CODE_RE.test(normalized)) return null;
  const res = await api(`/track/${encodeURIComponent(normalized)}`, { method: "POST", body: { kind, channel }, auth: false });
  if (res.clickId) saveReferral({ code: res.code, clickId: res.clickId, promoCode: res.promoCode });
  return res.destination;
}

/**
 * Called on every route change: a shared link like /tickets/group.details/123?ref=CODE
 * that didn't come through /r/:code still gets tracked. Skips if we already
 * hold this exact code (the /r/ hop already recorded the click).
 */
export function captureRefFromUrl(search) {
  const code = new URLSearchParams(search).get("ref");
  if (!code) return;
  const current = getActiveReferral();
  if (current?.code === code.toUpperCase()) return;
  trackReferral(code).catch(() => {});
}
